import type { Scope } from "./types";

/**
 * Human-readable summary of what a rule's scope targets. Deliberately avoids raw
 * ids and graph-model jargon (node/edge/property) — records/relationships/fields —
 * so it reads for a non-technical admin. Pass `recordName` (resolved from the
 * scope's node id) to name the specific record a one-off override targets.
 */
export function describeScope(scope: Scope, recordName?: string): string {
	switch (scope.kind) {
		case "global":
			return "Everywhere";
		case "node":
			return recordName
				? `${recordName} (this record only)`
				: "One specific record only";
		case "property":
			return recordName
				? `${recordName}'s "${scope.propId}" field`
				: `The "${scope.propId}" field on one specific record`;
		case "edge":
			return "One specific relationship only";
		case "edgeProp":
			return `The "${scope.propId}" field on one specific relationship`;
		case "byType":
			return `All ${scope.nodeType} records`;
		case "byTag":
			return `Fields tagged "${scope.tag}"`;
		case "property-name":
			return `The "${scope.name}" field on every record`;
		case "query": {
			const n = scope.expr.conditions.length;
			return `Records matching ${n} condition${n === 1 ? "" : "s"}`;
		}
	}
}

/** The record (node) id a one-off override targets, for name resolution. */
export function scopeRecordNodeId(scope: Scope): string | null {
	return scope.kind === "node" || scope.kind === "property"
		? scope.nodeId
		: null;
}

/** Which rules-tab filter bucket a scope belongs to. */
export type RuleFilter = "all" | "node" | "edge" | "query" | "global";

export function scopeFilterBucket(scope: Scope): Exclude<RuleFilter, "all"> {
	switch (scope.kind) {
		case "global":
			return "global";
		case "query":
			return "query";
		case "edge":
		case "edgeProp":
			return "edge";
		default:
			return "node";
	}
}
